import React from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../../components/blocks/AnimatedSection';
import TransdisciplinarityIntro from '../../components/blocks/TransdisciplinarityIntro';
import PartnerCard from '../../components/blocks/PartnerCard';
import FacultyAdvisorCard from '../../components/blocks/FacultyAdvisorCard';
import CortexAbstracts2026Publication from '../../components/blocks/CortexAbstracts2026Publication';
import {
    getCortexEventBySlug,
    getCortex2026KeynoteAdvisorKey,
    CORTEX_2026_KEYNOTE_FOOTER_BADGE,
} from '../../config/cortexEvents';
import { getPartnersByKeys, CONFERENCE_2026_PARTNER_KEYS } from '../../config/partners';
import { getAdvisorsByKeys, CORTEX_FACULTY_ADVISOR_KEYS } from '../../config/advisors';
import { cortexOutlineButtonClass } from '../../config/themeColors';

const Conference2026 = () => {
    const conferenceEvent = getCortexEventBySlug('conference-2026');
    const keynoteKey = getCortex2026KeynoteAdvisorKey();

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-16">
                    {conferenceEvent?.posterImage && (
                        <div className="mb-12 flex justify-center">
                            <img
                                src={encodeURI(conferenceEvent.posterImage)}
                                alt={conferenceEvent.posterAlt || 'CORTEX Conference 2026 Poster'}
                                className="w-auto h-auto max-w-md md:max-w-lg object-contain rounded-lg shadow-lg"
                            />
                        </div>
                    )}
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-8 mb-4">CORTEX Conference 2026</h1>
                    <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-6">
                        Conference for Research and Transdisciplinary Experiential Learning 2026
                    </p>
                    <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-6">
                        {conferenceEvent?.posterSubtitle || 'Submission: 25th June 2026 | Presentation: 4th July 2026'}
                    </p>
                    {conferenceEvent?.deadlineExtended && (
                        <div
                            role="status"
                            className="max-w-3xl mx-auto mb-8 rounded-xl border-2 border-amber-400 bg-amber-50 p-5 sm:p-6 text-left shadow-sm"
                        >
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                <span className="inline-flex rounded-full bg-amber-500 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                                    Deadline Extended
                                </span>
                                <span className="text-sm font-semibold text-amber-900">Update announcement</span>
                            </div>
                            <p className="text-gray-800 leading-relaxed text-justify mb-4">
                                {conferenceEvent.deadlineExtensionBlurb ||
                                    'The abstract submission deadline has been officially extended.'}
                            </p>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-gray-800">
                                <p>
                                    <span className="font-medium text-gray-600">Previous deadline: </span>
                                    <span className="line-through text-gray-500">
                                        {conferenceEvent.previousSubmissionDeadline || '21st June, 2026'}
                                    </span>
                                </p>
                                <p>
                                    <span className="font-medium text-gray-600">New deadline: </span>
                                    <span className="font-bold text-amber-900">
                                        {conferenceEvent.submissionDeadline || '25th June, 2026'}
                                    </span>
                                </p>
                            </div>
                            {conferenceEvent.deadlineExtensionInstagram && (
                                <a
                                    href={conferenceEvent.deadlineExtensionInstagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block mt-4 text-sm font-semibold text-amber-800 hover:text-amber-950 underline underline-offset-2"
                                >
                                    View announcement on Instagram →
                                </a>
                            )}
                        </div>
                    )}
                    <div className="max-w-3xl mx-auto mb-6 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3">
                        <a href="#publications" className={cortexOutlineButtonClass}>
                            Book of Abstracts 2026
                        </a>
                        <Link to="/cortex/keynote-speech-2026" className={cortexOutlineButtonClass}>
                            Keynote Speech — abstract &amp; bibliography
                        </Link>
                        <a
                            href="https://forms.gle/vinGcj181MLXHzSK7"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={cortexOutlineButtonClass}
                        >
                            Submit an Abstract
                        </a>
                    </div>
                </div>

                <TransdisciplinarityIntro />

                {/* Overview Section */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Overview</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <p className="text-lg text-gray-700 leading-relaxed text-justify mb-6">
                            The CORTEX Conference is a student-led platform for undergraduates to present and publish their
                            transdisciplinary ideas. We welcome regular conference papers, research projects, experiential
                            reports, and undergraduate theses that integrate insights across disciplines.
                        </p>
                        <div className="grid md:grid-cols-2 gap-6 mt-6">
                            <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Conference Date</h3>
                                <p className="text-gray-700">4th July, 2026</p>
                            </div>
                            <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Submission Deadline</h3>
                                {conferenceEvent?.deadlineExtended ? (
                                    <div className="space-y-2">
                                        <p className="text-gray-500 line-through">
                                            {conferenceEvent.previousSubmissionDeadline || '21st June, 2026'}
                                        </p>
                                        <p className="text-gray-900 font-semibold">
                                            {conferenceEvent.submissionDeadline || '25th June, 2026'}
                                            <span className="ml-2 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-amber-800 align-middle">
                                                Extended
                                            </span>
                                        </p>
                                    </div>
                                ) : (
                                    <p className="text-gray-700">
                                        {conferenceEvent?.submissionDeadline || '25th June, 2026'}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-6 mt-6">
                            <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Presentation Format</h3>
                                <p className="text-gray-700">15-minute presentation + 5-minute Q&amp;A</p>
                            </div>
                            <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Venue</h3>
                                <p className="text-gray-700">
                                    {conferenceEvent?.venue || 'Rm 4.35, XR Space, Arts Tech Lab, RRS Tower, Centennial Campus, HKU'}
                                </p>
                            </div>
                        </div>
                        
                        {/* Our Partners */}
                        <div className="mt-8">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Partners</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 items-stretch justify-items-center">
                                {getPartnersByKeys(CONFERENCE_2026_PARTNER_KEYS).map((partner) => (
                                    <PartnerCard
                                        key={partner.id}
                                        partner={partner}
                                        className="p-6 rounded-xl w-full md:max-w-xs min-h-[200px]"
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </AnimatedSection>

                {/* Faculty advisors — same seniority order as /cortex; keynote is rim-highlighted only */}
                <AnimatedSection
                    className="mb-16"
                    heading={
                        <h2 className="text-3xl font-semibold text-gray-900 mb-8">Faculty Advisors</h2>
                    }
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <p className="text-lg text-gray-700 leading-relaxed mb-6 text-justify">
                            To ensure the academic rigour and transdisciplinary initiatives of CORTEX, faculty advisors from
                            the University of Hong Kong and partner institutions provide academic guidance and support to ensure
                            the rigour and quality of CORTEX initiatives.
                        </p>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {CORTEX_FACULTY_ADVISOR_KEYS.map((advisorKey) => {
                                const advisor = getAdvisorsByKeys([advisorKey], 'blue')[0];
                                if (!advisor) return null;
                                const isKeynote = Boolean(keynoteKey && advisorKey === keynoteKey);
                                return (
                                    <FacultyAdvisorCard
                                        key={advisorKey}
                                        {...advisor}
                                        rimHighlight={isKeynote}
                                        footerBadge={isKeynote ? CORTEX_2026_KEYNOTE_FOOTER_BADGE : undefined}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </AnimatedSection>

                {/* Conference Details */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Conference Details</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className="space-y-6">
                            <div className="border-l-4 border-blue-500 pl-6 rounded-lg transition-all duration-200 hover:bg-blue-50 hover:border-blue-600 hover:shadow-md cursor-pointer">
                                <div className="flex items-start space-x-4">
                                    <div className="flex-1 pt-4 pb-6">
                                        <h3 className="text-xl font-semibold text-gray-900 mt-2 mb-2">Theme</h3>
                                        <p className="text-gray-700 leading-relaxed text-justify">
                                            We accept academic work on any topic that aligns with the theme of transdisciplinarity.
                                        </p>
                                        <p className="text-gray-700 leading-relaxed text-justify">
                                            <i>Transdisciplinary</i> refers to the integration of perspectives and methods across disciplines to address complex real-world issues.
                                        </p>
                                        <div className="mt-5 bg-gray-50 rounded-lg p-4 transition-colors duration-200 hover:bg-gray-100">
                                            <h4 className="text-lg font-semibold text-gray-900 mb-2">
                                                Recommended (but not limited to) transdisciplinary sub-themes
                                            </h4>
                                            <ul className="list-disc list-inside space-y-1 text-gray-700">
                                                <li>AI, Big Data and Society</li>
                                                <li>Law, Humanities and Global Challenges</li>
                                                <li>China, Culture and Heritage in a Changing World</li>
                                                <li>Smart cities and Urban Ecologies</li>
                                                <li>Scientific Innovation and Industry in the modern world</li>
                                                <li>Biological and Medical Sciences for Global Good</li>
                                                <li>General transdisciplinary themes</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="border-l-4 border-green-500 pl-6 rounded-lg transition-all duration-200 hover:bg-green-50 hover:border-green-600 hover:shadow-md cursor-pointer">
                                <div className="flex items-start space-x-4">
                                    <div className="flex-1 pt-4 pb-6">
                                        <h3 className="text-xl font-semibold text-gray-900 mt-2 mb-2">Eligible Submissions</h3>
                                        <p className="text-gray-700 leading-relaxed text-justify">
                                            We accept works from any students studying at a formal academic institution at a tertiary, undergraduate level. We welcome any conference papers, research projects, experiential learning reports, and undergraduate theses that demonstrate transdisciplinary thinking.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="border-l-4 border-purple-500 pl-6 rounded-lg transition-all duration-200 hover:bg-purple-50 hover:border-purple-600 hover:shadow-md cursor-pointer">
                                <div className="flex items-start space-x-4">
                                    <div className="flex-1 pt-4 pb-6">
                                        <h3 className="text-xl font-semibold text-gray-900 mt-2 mb-2">Abstract Requirements</h3>
                                        <div className="space-y-2 text-gray-700">   
                                            <p className="text-gray-700 leading-relaxed text-justify">
                                                The length of the abstract should range <b>between 250 and 300 words</b>. It should cover the following particulars: background, aim, methodology, and summary of findings or expected contributions. Submissions are now open until{' '}
                                                {conferenceEvent?.submissionDeadline || '25th June, 2026'}
                                                {conferenceEvent?.deadlineExtended && (
                                                    <>
                                                        {' '}
                                                        <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-amber-800 align-middle">
                                                            Extended
                                                        </span>
                                                    </>
                                                )}
                                                .
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </AnimatedSection>

                <CortexAbstracts2026Publication />

                {/* Venue */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Venue</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className="grid md:grid-cols-2 gap-8 items-center">
                            <div className="rounded-2xl overflow-hidden aspect-video w-full ring-1 ring-gray-200 shadow-sm">
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3691.8!2d114.1378!3d22.2833!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3404005c4b8b8b8b%3A0x8b8b8b8b8b8b8b8b!2sThe%20University%20of%20Hong%20Kong!5e0!3m2!1sen!2shk!4v1234567890123!5m2!1sen!2shk"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="Location of the University of Hong Kong"
                                />
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900">Arts Tech Lab, XR Space</h3>
                                <p className="text-gray-700">
                                    {conferenceEvent?.venue || 'Rm 4.35, XR Space, Arts Tech Lab, RRS Tower, Centennial Campus, HKU'}
                                </p>
                                <p className="text-gray-600 mt-1">Centennial Campus, The University of Hong Kong</p>
                                <p className="mt-4 text-gray-700 leading-relaxed text-justify">
                                    The University of Hong Kong, Asia's Global University, delivers impact through internationalisation, innovation and interdisciplinarity. It attracts and nurtures global scholars through excellence in research, teaching and learning, and knowledge exchange.
                                </p>
                            </div>
                        </div>
                    </div>
                </AnimatedSection>
            </div>
        </div>
    );
};

export default Conference2026;
